import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Calendar, ChevronDown, Check, Copy, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface CalendarButtonsProps {
  appointment: {
    start_time: string;
    end_time: string;
    title: string;
    description?: string;
    location?: string;
  };
  practitionerName: string;
  clinicName?: string;
  telehealthLink?: string;
}

// Generate ICS file content
const generateICS = (appointment: CalendarButtonsProps['appointment']): string => {
  const formatICSDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);

  const escapeICS = (text: string): string => {
    return text.replace(/[,;\\]/g, m => '\\' + m).replace(/\n/g, '\\n');
  };

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//NOVAMED//Booking System//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `DTSTART:${formatICSDate(start)}`,
    `DTEND:${formatICSDate(end)}`,
    `SUMMARY:${escapeICS(appointment.title)}`,
  ];

  if (appointment.description) {
    lines.push(`DESCRIPTION:${escapeICS(appointment.description)}`);
  }

  if (appointment.location) {
    lines.push(`LOCATION:${escapeICS(appointment.location)}`);
  }

  lines.push(
    `UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@novamed.com`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    'STATUS:CONFIRMED',
    'END:VEVENT',
    'END:VCALENDAR'
  );

  return lines.join('\r\n');
};

// Generate Google Calendar URL
const generateGoogleCalendarUrl = (appointment: CalendarButtonsProps['appointment']): string => {
  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);

  const formatGoogleDate = (date: Date): string => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: appointment.title,
    dates: `${formatGoogleDate(start)}/${formatGoogleDate(end)}`,
    details: appointment.description || '',
    location: appointment.location || '',
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

// Generate Outlook.com URL
const generateOutlookUrl = (appointment: CalendarButtonsProps['appointment']): string => {
  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: appointment.title,
    startdt: start.toISOString(),
    enddt: end.toISOString(),
    body: appointment.description || '',
    location: appointment.location || '',
  });

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
};

// Generate Yahoo Calendar URL
const generateYahooUrl = (appointment: CalendarButtonsProps['appointment']): string => {
  const start = new Date(appointment.start_time);
  const end = new Date(appointment.end_time);

  const formatYahooDate = (date: Date): string => {
    return date.toISOString().replace(/[-:.]/g, '').split('Z')[0] + 'Z';
  };

  // Calculate duration in hours and minutes
  const durationMs = end.getTime() - start.getTime();
  const durationHours = Math.floor(durationMs / (1000 * 60 * 60));
  const durationMinutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
  const duration = `${durationHours.toString().padStart(2, '0')}${durationMinutes.toString().padStart(2, '0')}`;

  const params = new URLSearchParams({
    v: '60',
    title: appointment.title,
    st: formatYahooDate(start),
    dur: duration,
    desc: appointment.description || '',
    in_loc: appointment.location || '',
  });

  return `https://calendar.yahoo.com/?${params.toString()}`;
};

export const CalendarButtons: React.FC<CalendarButtonsProps> = ({
  appointment,
  practitionerName,
  clinicName,
  telehealthLink,
}) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  // Build appointment data
  const eventData = {
    start_time: appointment.start_time,
    end_time: appointment.end_time,
    title: `Appointment with ${practitionerName}`,
    description: telehealthLink
      ? `Video call link: ${telehealthLink}\n\n${clinicName || ''}`
      : clinicName || '',
    location: telehealthLink || clinicName || '',
  };

  // Download ICS file
  const handleDownloadICS = () => {
    const icsContent = generateICS(eventData);
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = 'appointment.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: 'Downloaded',
      description: 'Calendar file downloaded. Open it to add to your calendar.',
    });
  };

  // Open Google Calendar
  const handleGoogleCalendar = () => {
    window.open(generateGoogleCalendarUrl(eventData), '_blank');
  };

  // Open Outlook.com
  const handleOutlook = () => {
    window.open(generateOutlookUrl(eventData), '_blank');
  };

  // Open Yahoo Calendar
  const handleYahoo = () => {
    window.open(generateYahooUrl(eventData), '_blank');
  };

  // Copy event details
  const handleCopyDetails = () => {
    const start = new Date(appointment.start_time);
    const details = [
      eventData.title,
      '',
      `📅 ${start.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`,
      `🕐 ${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}`,
      clinicName ? `📍 ${clinicName}` : '',
      telehealthLink ? `🔗 ${telehealthLink}` : '',
    ]
      .filter(Boolean)
      .join('\n');

    navigator.clipboard.writeText(details);
    setCopied(true);
    toast({
      title: 'Copied',
      description: 'Event details copied to clipboard',
    });

    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className='flex flex-col gap-3 sm:flex-row'>
      {/* Add to Calendar Dropdown */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline' className='flex-1'>
            <Calendar className='mr-2 h-4 w-4' />
            Add to Calendar
            <ChevronDown className='ml-2 h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='center' className='w-48'>
          <DropdownMenuItem onClick={handleGoogleCalendar}>
            <div className='flex items-center gap-2'>
              <svg viewBox='0 0 24 24' className='h-4 w-4'>
                <path
                  fill='#4285F4'
                  d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
                />
                <path
                  fill='#34A853'
                  d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
                />
                <path
                  fill='#FBBC05'
                  d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
                />
                <path
                  fill='#EA4335'
                  d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
                />
              </svg>
              <span>Google Calendar</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleOutlook}>
            <div className='flex items-center gap-2'>
              <svg viewBox='0 0 24 24' className='h-4 w-4'>
                <path
                  fill='#0078D4'
                  d='M24 7.387v10.478c0 .23-.08.424-.238.576-.16.152-.356.228-.59.228h-8.547v-6.453l1.602 1.172c.078.059.18.088.305.088.127 0 .23-.03.313-.088l7.156-5.236V7.387zm0-.715c0-.301-.147-.544-.441-.727-.295-.183-.577-.274-.848-.274h-.274L14.625 11.5l-1.984-1.465V3.331h-1.282v.652H1.625c-.234 0-.434.076-.598.228-.164.153-.246.344-.246.574v12.133c0 .234.082.43.246.586.164.157.364.235.598.235h9.734V6.672l1.266-.938 2.688 1.965 9.687-7.027z'
                />
              </svg>
              <span>Outlook</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleYahoo}>
            <div className='flex items-center gap-2'>
              <svg viewBox='0 0 24 24' className='h-4 w-4'>
                <path
                  fill='#6001D2'
                  d='M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 21.6c-5.302 0-9.6-4.298-9.6-9.6S6.698 2.4 12 2.4s9.6 4.298 9.6 9.6-4.298 9.6-9.6 9.6z'
                />
                <path
                  fill='#6001D2'
                  d='M12.6 6l-2.4 5.4-2.4-5.4H5.4l3.6 7.2v4.8h2.4v-4.8l3.6-7.2h-2.4z'
                />
              </svg>
              <span>Yahoo Calendar</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDownloadICS}>
            <div className='flex items-center gap-2'>
              <Calendar className='h-4 w-4' />
              <span>Apple / Outlook (.ics)</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Copy Details Button */}
      <Button variant='outline' className='flex-1' onClick={handleCopyDetails}>
        {copied ? (
          <>
            <Check className='mr-2 h-4 w-4 text-green-600' />
            Copied!
          </>
        ) : (
          <>
            <Copy className='mr-2 h-4 w-4' />
            Copy Details
          </>
        )}
      </Button>
    </div>
  );
};

export default CalendarButtons;

